import { useState } from 'react'
import type { ScoreLetter } from '@/domains/types'

const GRADES: ScoreLetter[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G']
const GRADE_SET = new Set<string>(GRADES)
function isScoreLetter(value: string | null | undefined): value is ScoreLetter {
  return value !== null && value !== undefined && GRADE_SET.has(value)
}

const GRADE_STYLE: Record<
  ScoreLetter,
  { bg: string; activeBg: string; text: string; activeText: string; hoverBg: string }
> = {
  A: {
    bg: '#d4edda',
    activeBg: '#00703f',
    text: '#00703f',
    activeText: '#fff',
    hoverBg: '#b8dfc4',
  },
  B: {
    bg: '#dff0c8',
    activeBg: '#88c340',
    text: '#5a8a1e',
    activeText: '#fff',
    hoverBg: '#c8e5a0',
  },
  C: {
    bg: '#fef9cc',
    activeBg: '#fecb02',
    text: '#7a6200',
    activeText: '#333',
    hoverBg: '#fdf0a0',
  },
  D: {
    bg: '#fde8c8',
    activeBg: '#ef8200',
    text: '#7a4000',
    activeText: '#fff',
    hoverBg: '#f9d5a0',
  },
  E: {
    bg: '#fdd9cc',
    activeBg: '#e63512',
    text: '#9a1f08',
    activeText: '#fff',
    hoverBg: '#f9c0a8',
  },
  F: {
    bg: '#f5c6c0',
    activeBg: '#c0392b',
    text: '#7a1c15',
    activeText: '#fff',
    hoverBg: '#eeaaa0',
  },
  G: {
    bg: '#e8b8b5',
    activeBg: '#8b1a1a',
    text: '#5c1212',
    activeText: '#fff',
    hoverBg: '#d89a95',
  },
}

interface NutriScoreProps {
  grade: ScoreLetter | string | null | undefined
  size?: 'sm' | 'md' | 'lg'
  /** Show "NutriScore" label above the scale */
  showLabel?: boolean
  /** When provided, each grade becomes a clickable button */
  onSelect?: (grade: ScoreLetter) => void
}

const SIZE_CONFIG = {
  sm: { cell: 'w-5 h-6 text-xs', active: 'w-6 h-8 text-sm font-bold' },
  md: { cell: 'w-7 h-9 text-sm', active: 'w-9 h-12 text-base font-bold' },
  lg: { cell: 'w-9 h-12 text-base', active: 'w-12 h-16 text-xl font-bold' },
}

export function NutriScore({ grade, size = 'md', showLabel = false, onSelect }: NutriScoreProps) {
  const upper = grade?.toUpperCase() ?? null
  const activeGrade: ScoreLetter | null = isScoreLetter(upper) ? upper : null
  const { cell, active } = SIZE_CONFIG[size]
  const interactive = !!onSelect
  const [hoveredGrade, setHoveredGrade] = useState<ScoreLetter | null>(null)

  return (
    <div className="inline-flex flex-col items-center gap-1">
      {showLabel && (
        <span className="text-xs font-semibold text-muted-foreground tracking-widest uppercase">
          Nutri-Score
        </span>
      )}
      <div className="flex items-end gap-0.5">
        {GRADES.map((letter) => {
          const isActive = letter === activeGrade
          const isHovered = interactive && letter === hoveredGrade
          const highlighted = isActive || isHovered
          const style = GRADE_STYLE[letter]

          const letterStyle = {
            backgroundColor: highlighted ? style.activeBg : style.bg,
            color: highlighted ? style.activeText : style.text,
          }
          const letterClass = `
            flex items-center justify-center rounded font-semibold leading-none transition-all
            ${highlighted ? active : cell}
            ${interactive ? 'cursor-pointer' : ''}
          `

          if (interactive) {
            return (
              <button
                key={letter}
                type="button"
                title={letter}
                style={letterStyle}
                className={letterClass}
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  onSelect(letter)
                }}
                onMouseEnter={() => setHoveredGrade(letter)}
                onMouseLeave={() => setHoveredGrade(null)}
              >
                {letter}
              </button>
            )
          }

          return (
            <div key={letter} title={letter} style={letterStyle} className={letterClass}>
              {letter}
            </div>
          )
        })}
      </div>
    </div>
  )
}
